import { useEffect,useState } from "react";
import {
  Box,Typography,Button,TextField,CircularProgress,
  Table,TableBody,TableCell,TableContainer,TableHead,TableRow,
  Dialog,DialogTitle,DialogContent,DialogActions,Switch,FormControlLabel
} from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find(row=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";

  if(contentType.includes("application/json")){
    return response.json();
  }

  if(!response.ok){
    throw new Error("El servidor no respondió correctamente.");
  }

  return {};
};

const formatDate=(value)=>{
  if(!value) return "—";

  const date=new Date(value);

  if(Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("es-EC",{
    day:"2-digit",
    month:"2-digit",
    year:"numeric",
    hour:"2-digit",
    minute:"2-digit",
  });
};

const toBackendDate=(value)=>{
  if(!value) return null;

  const date=new Date(value);

  if(Number.isNaN(date.getTime())) return null;

  return date.toISOString();
};

const toLocalInput=(value)=>{
  if(!value) return "";

  const date=new Date(value);

  if(Number.isNaN(date.getTime())) return "";

  const offset=date.getTimezoneOffset();
  const localDate=new Date(date.getTime()-offset*60000);

  return localDate.toISOString().slice(0,16);
};

const getWindowState=(item)=>{
  if(item.status==="OPEN") return "Abierto";
  if(item.status==="SCHEDULED") return "Programado";
  if(item.status==="CLOSED") return "Finalizado";
  if(item.status==="INACTIVE") return "Inactivo";

  if(!item.active) return "Inactivo";

  const now=new Date();
  const start=new Date(item.starts_at);
  const end=new Date(item.ends_at);

  if(now<start) return "Programado";
  if(now>end) return "Finalizado";

  return "Abierto";
};

export default function AdminTransferMarket(){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [windows,setWindows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [deleting,setDeleting]=useState(false);

  const [formOpen,setFormOpen]=useState(false);
  const [deleteOpen,setDeleteOpen]=useState(false);

  const [selectedWindow,setSelectedWindow]=useState(null);

  const [form,setForm]=useState({
    name:"",
    starts_at:"",
    ends_at:"",
    active:true,
  });

  const loadWindows=async()=>{
    try{
      setLoading(true);

      const response=await fetch("/competition/registration/windows/",{
        credentials:"include",
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudieron cargar los períodos de fichajes.");
      }

      setWindows(Array.isArray(data)?data:[]);
    }catch(error){
      showError(error.message||"No se pudieron cargar los períodos de fichajes.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadWindows();
  },[]);

  const openCreate=()=>{
    setSelectedWindow(null);

    setForm({
      name:"",
      starts_at:"",
      ends_at:"",
      active:true,
    });

    setFormOpen(true);
  };

  const openEdit=(item)=>{
    setSelectedWindow(item);

    setForm({
      name:item.name||"",
      starts_at:toLocalInput(item.starts_at),
      ends_at:toLocalInput(item.ends_at),
      active:Boolean(item.active),
    });

    setFormOpen(true);
  };

  const closeForm=()=>{
    if(saving) return;

    setFormOpen(false);
    setSelectedWindow(null);
  };

  const openDelete=(item)=>{
    if(!item.can_delete){
      showError("Este período ya fue utilizado y no puede eliminarse.");
      return;
    }

    setSelectedWindow(item);
    setDeleteOpen(true);
  };

  const closeDelete=()=>{
    if(deleting) return;

    setDeleteOpen(false);
    setSelectedWindow(null);
  };

  const handleChange=(event)=>{
    const {name,value}=event.target;

    setForm(current=>({
      ...current,
      [name]:value,
    }));
  };

  const validateForm=()=>{
    if(selectedWindow?.has_been_used){
      return true;
    }

    if(!form.name.trim()){
      showError("Ingresa el nombre del período.");
      return false;
    }

    if(!form.starts_at){
      showError("Selecciona la fecha de apertura.");
      return false;
    }

    if(!form.ends_at){
      showError("Selecciona la fecha de cierre.");
      return false;
    }

    const start=new Date(form.starts_at);
    const end=new Date(form.ends_at);

    if(Number.isNaN(start.getTime())||Number.isNaN(end.getTime())){
      showError("Las fechas ingresadas no son válidas.");
      return false;
    }

    if(end<=start){
      showError("La fecha de cierre debe ser posterior a la fecha de apertura.");
      return false;
    }

    return true;
  };

  const handleSave=async()=>{
    if(!validateForm()) return;

    try{
      setSaving(true);

      const editing=Boolean(selectedWindow);

      const url=editing
        ? `/competition/registration/windows/${selectedWindow.slug}/`
        : "/competition/registration/windows/";

      let payload;

      if(editing&&selectedWindow.has_been_used){
        payload={
          active:form.active,
        };
      }else{
        payload={
          name:form.name.trim(),
          starts_at:toBackendDate(form.starts_at),
          ends_at:toBackendDate(form.ends_at),
          active:form.active,
        };
      }

      const response=await fetch(url,{
        method:editing?"PATCH":"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken"),
        },
        body:JSON.stringify(payload),
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          (editing
            ?"No se pudo actualizar el período."
            :"No se pudo registrar el período.")
        );
      }

      setFormOpen(false);
      setSelectedWindow(null);

      showSuccess(
        editing
          ?"Período de fichajes actualizado correctamente."
          :"Período de fichajes registrado correctamente."
      );

      await loadWindows();
    }catch(error){
      showError(error.message||"No se pudo guardar el período.");
    }finally{
      setSaving(false);
    }
  };

  const handleDelete=async()=>{
    if(!selectedWindow) return;

    if(!selectedWindow.can_delete){
      showError("Este período ya fue utilizado y no puede eliminarse.");
      setDeleteOpen(false);
      setSelectedWindow(null);
      return;
    }

    try{
      setDeleting(true);

      const response=await fetch(
        `/competition/registration/windows/${selectedWindow.slug}/`,
        {
          method:"DELETE",
          credentials:"include",
          headers:{
            "X-CSRFToken":getCookie("csrftoken"),
          },
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudo eliminar el período.");
      }

      setDeleteOpen(false);
      setSelectedWindow(null);

      showSuccess("Período de fichajes eliminado correctamente.");

      await loadWindows();
    }catch(error){
      showError(error.message||"No se pudo eliminar el período.");
    }finally{
      setDeleting(false);
    }
  };

  const headCellSx={
    py:1.2,
    px:1.5,
    backgroundColor:colors.tableHeader,
    color:colors.tableHeaderText,
    fontSize:typography.sizes.sm,
    fontWeight:typography.weights.bold,
    borderColor:colors.border,
    whiteSpace:"nowrap",
  };

  const bodyCellSx={
    py:1.2,
    px:1.5,
    color:colors.text,
    fontSize:typography.sizes.sm,
    borderColor:colors.border,
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Mercado de fichajes
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary,
            }}
          >
            Administra los períodos habilitados para el registro y actualización de jugadores.
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          onClick={openCreate}
          sx={{
            px:2.2,
            alignSelf:{xs:"flex-start",sm:"center"},
            backgroundColor:colors.secondary,
            color:colors.buttonPrimaryText,
            boxShadow:"none",
            "&:hover":{
              backgroundColor:colors.secondaryHover,
              boxShadow:"none",
            },
          }}
        >
          Nuevo período
        </Button>
      </Box>

      {loading?(
        <Box
          sx={{
            minHeight:220,
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radiusLarge,
            backgroundColor:colors.surface,
          }}
        >
          <CircularProgress size={24} sx={{color:colors.secondary}} />
        </Box>
      ):(
        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radiusLarge,
            backgroundColor:colors.surface,
            overflowX:"auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Período</TableCell>
                <TableCell sx={headCellSx}>Apertura</TableCell>
                <TableCell sx={headCellSx}>Cierre</TableCell>
                <TableCell sx={headCellSx}>Estado</TableCell>
                <TableCell sx={{...headCellSx,textAlign:"right"}}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {windows.length===0?(
                <TableRow>
                  <TableCell colSpan={5} sx={{py:5,textAlign:"center",border:0}}>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.md,
                        color:colors.textSecondary,
                      }}
                    >
                      No existen períodos de fichajes registrados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ):windows.map(item=>{
                const state=getWindowState(item);

                return (
                  <TableRow
                    key={item.slug}
                    sx={{
                      "&:last-child td":{borderBottom:0},
                      "&:hover":{backgroundColor:colors.backgroundSoft},
                    }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        fontWeight:typography.weights.semibold,
                      }}
                    >
                      {item.name}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {formatDate(item.starts_at)}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {formatDate(item.ends_at)}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize:typography.sizes.sm,
                          fontWeight:typography.weights.semibold,
                          color:
                            state==="Abierto"
                              ? colors.success
                              : state==="Programado"
                                ? colors.info
                                : state==="Finalizado"
                                  ? colors.textMuted
                                  : colors.textSecondary,
                        }}
                      >
                        {state}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{...bodyCellSx,textAlign:"right",whiteSpace:"nowrap"}}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={()=>openEdit(item)}
                        sx={{
                          minWidth:"auto",
                          px:1.2,
                          color:colors.secondary,
                          textTransform:"none",
                          fontWeight:typography.weights.semibold,
                          "&:hover":{
                            backgroundColor:colors.backgroundSoft,
                          },
                        }}
                      >
                        Editar
                      </Button>

                      {item.can_delete&&(
                        <Button
                          size="small"
                          variant="text"
                          onClick={()=>openDelete(item)}
                          sx={{
                            minWidth:"auto",
                            ml:0.5,
                            px:1.2,
                            color:colors.error,
                            textTransform:"none",
                            fontWeight:typography.weights.semibold,
                            "&:hover":{
                              backgroundColor:colors.backgroundSoft,
                            },
                          }}
                        >
                          Eliminar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
          },
        }}
      >
        <DialogTitle sx={{pb:1}}>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            {selectedWindow?"Editar período de fichajes":"Nuevo período de fichajes"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {selectedWindow?.has_been_used&&(
            <Box
              sx={{
                mb:2,
                px:1.5,
                py:1.2,
                border:`1px solid ${colors.border}`,
                borderRadius:shape.radiusSmall,
                backgroundColor:colors.backgroundSoft,
              }}
            >
              <Typography
                sx={{
                  fontSize:typography.sizes.sm,
                  fontWeight:typography.weights.semibold,
                  color:colors.text,
                }}
              >
                Este período ya fue utilizado
              </Typography>

              <Typography
                sx={{
                  mt:0.3,
                  fontSize:typography.sizes.sm,
                  color:colors.textSecondary,
                }}
              >
                Para conservar el historial de inscripciones no puedes modificar su nombre ni sus fechas. Solo puedes cambiar su estado.
              </Typography>
            </Box>
          )}

          <TextField
            size="small"
            label="Nombre del período"
            name="name"
            value={form.name}
            onChange={handleChange}
            disabled={Boolean(selectedWindow?.has_been_used)}
            placeholder="Ej. Mercado de fichajes 2026"
            fullWidth
            sx={{mb:1.5}}
          />

          <Box
            sx={{
              display:"grid",
              gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},
              gap:1.5,
            }}
          >
            <TextField
              size="small"
              type="datetime-local"
              label="Apertura"
              name="starts_at"
              value={form.starts_at}
              onChange={handleChange}
              disabled={Boolean(selectedWindow?.has_been_used)}
              InputLabelProps={{shrink:true}}
              fullWidth
            />

            <TextField
              size="small"
              type="datetime-local"
              label="Cierre"
              name="ends_at"
              value={form.ends_at}
              onChange={handleChange}
              disabled={Boolean(selectedWindow?.has_been_used)}
              InputLabelProps={{shrink:true}}
              fullWidth
            />
          </Box>

          <FormControlLabel
            sx={{mt:1.3,ml:0}}
            control={
              <Switch
                size="small"
                checked={form.active}
                onChange={event=>setForm(current=>({
                  ...current,
                  active:event.target.checked,
                }))}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked":{
                    color:colors.controlActive,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{
                    backgroundColor:colors.controlActive,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  ml:0.5,
                  fontSize:typography.sizes.sm,
                  color:colors.text,
                }}
              >
                Período habilitado
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            px:3,
            py:2,
            borderTop:`1px solid ${colors.border}`,
            gap:1,
          }}
        >
          <Button
            size="small"
            onClick={closeForm}
            disabled={saving}
            sx={{
              color:colors.buttonText,
              textTransform:"none",
              "&:hover":{
                color:colors.buttonTextHover,
                backgroundColor:colors.buttonTextHoverBackground,
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth:90,
              backgroundColor:colors.secondary,
              color:colors.buttonPrimaryText,
              boxShadow:"none",
              textTransform:"none",
              "&:hover":{
                backgroundColor:colors.secondaryHover,
                boxShadow:"none",
              },
            }}
          >
            {saving
              ? <CircularProgress size={18} sx={{color:colors.buttonPrimaryText}} />
              : selectedWindow?"Guardar cambios":"Guardar"
            }
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        fullWidth
        maxWidth="xs"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
          },
        }}
      >
        <DialogTitle sx={{pb:1}}>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Eliminar período
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              fontSize:typography.sizes.md,
              color:colors.textSecondary,
              lineHeight:1.5,
            }}
          >
            ¿Deseas eliminar el período <strong>{selectedWindow?.name}</strong>? Esta acción solo está disponible porque todavía no ha sido utilizado para registrar jugadores.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px:3,
            py:2,
            borderTop:`1px solid ${colors.border}`,
            gap:1,
          }}
        >
          <Button
            size="small"
            onClick={closeDelete}
            disabled={deleting}
            sx={{
              color:colors.buttonText,
              textTransform:"none",
              "&:hover":{
                color:colors.buttonTextHover,
                backgroundColor:colors.buttonTextHoverBackground,
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              minWidth:90,
              backgroundColor:colors.error,
              color:colors.buttonPrimaryText,
              boxShadow:"none",
              textTransform:"none",
              "&:hover":{
                backgroundColor:colors.error,
                boxShadow:"none",
              },
            }}
          >
            {deleting
              ? <CircularProgress size={18} sx={{color:colors.buttonPrimaryText}} />
              :"Eliminar"
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}